import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { surveyAPI } from '../../services/api';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { toast } from 'react-toastify';
import { CheckCircle, Loader, FileText, ArrowRight, ArrowLeft } from 'lucide-react';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.background.primary};
`;

const Content = styled.div`
  flex: 1;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  padding: 2rem;
`;

const SurveyCard = styled(Card)`
  padding: 2rem;
  margin-bottom: 2rem;
`;

const SurveyTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const SurveyDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  margin: 2rem 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${props => props.theme.colors.primary};
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const QuestionCard = styled(Card)`
  padding: 2rem;
  margin-bottom: 1.5rem;
`;

const QuestionText = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
`;

const RequiredBadge = styled.span`
  color: ${props => props.theme.colors.error};
  margin-left: 0.25rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const HelpText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0.5rem 0 1rem 0;
  font-style: italic;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.colors.background.secondary};
    border-color: ${props => props.theme.colors.primary};
  }
  
  input[type="radio"]:checked + &,
  input[type="checkbox"]:checked + & {
    background-color: ${props => props.theme.colors.primary}10;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.typography.fontSize.base};
  margin-top: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 3rem;
`;

const SurveyListContainer = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const SurveyListItem = styled(Card)`
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const SurveyPage = () => {
  const { theme } = useTheme();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [currentSurvey, setCurrentSurvey] = useState(null);
  const [surveyStarted, setSurveyStarted] = useState(false); // Track if user clicked "Attend"
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [respondentInfo, setRespondentInfo] = useState({
    name: '',
    email: '',
    phone: '',
    custom: {},
  });

  useEffect(() => {
    // Reset survey state when slug changes
    if (slug) {
      setCurrentSurvey(null);
      setSurveyStarted(false);
      fetchSurveyBySlug();
    } else {
      setCurrentSurvey(null);
      setSurveyStarted(false);
      fetchAvailableSurveys();
    }
  }, [slug]);

  const fetchAvailableSurveys = async () => {
    try {
      setLoading(true);
      const response = await surveyAPI.getAvailable();
      console.log('Survey API response:', response);
      console.log('Response data:', response.data);
      // Handle both direct array response and nested data response
      const surveysData = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
      console.log('Processed surveys:', surveysData);
      setSurveys(surveysData);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyBySlug = async () => {
    try {
      setLoading(true);
      const response = await surveyAPI.getBySlug(slug);
      console.log('Survey fetched by slug:', response.data);
      setCurrentSurvey(response.data);
      setSurveyStarted(false); // Reset to show intro page
      setCurrentQuestionIndex(0);
      setResponses({});
      setRespondentInfo({
        name: '',
        email: '',
        phone: '',
        custom: {},
      });
      console.log('Survey state reset - surveyStarted set to false, currentSurvey:', response.data?.title);
    } catch (error) {
      console.error('Error fetching survey:', error);
      toast.error('Survey not found or not available');
      navigate('/surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSurvey = () => {
    setSurveyStarted(true);
    setCurrentQuestionIndex(0);
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < currentSurvey.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate respondent info if required
    if (currentSurvey.collect_respondent_info) {
      if (currentSurvey.require_name && !respondentInfo.name) {
        toast.error('Please enter your name');
        setCurrentQuestionIndex(0);
        return;
      }
      if (currentSurvey.require_email && !respondentInfo.email) {
        toast.error('Please enter your email');
        setCurrentQuestionIndex(0);
        return;
      }
      if (currentSurvey.require_phone && !respondentInfo.phone) {
        toast.error('Please enter your phone number');
        setCurrentQuestionIndex(0);
        return;
      }
      
      // Validate custom required fields
      if (currentSurvey.custom_respondent_fields) {
        const missingCustom = currentSurvey.custom_respondent_fields.filter(
          field => field.required && !respondentInfo.custom[field.name]
        );
        if (missingCustom.length > 0) {
          toast.error(`Please fill in required field: ${missingCustom[0].label}`);
          setCurrentQuestionIndex(0);
          return;
        }
      }
    }
    
    // Validate required questions
    const requiredQuestions = currentSurvey.questions.filter(q => q.is_required);
    const missingRequired = requiredQuestions.filter(q => !responses[q.id]);

    if (missingRequired.length > 0) {
      toast.error('Please answer all required questions');
      // Jump to first missing question
      const firstMissingIndex = currentSurvey.questions.findIndex(q => q.id === missingRequired[0].id);
      setCurrentQuestionIndex(firstMissingIndex);
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare response data - ensure proper formatting
      const responseData = currentSurvey.questions.map(question => {
        const responseValue = responses[question.id];
        
        console.log('Preparing response for question:', {
          question_id: question.id,
          question_type: question.question_type,
          question_text: question.question_text,
          responseValue: responseValue,
          responseValueType: typeof responseValue
        });
        
        // For checkbox, ensure it's always an array
        if (question.question_type === 'checkbox') {
          const value = Array.isArray(responseValue) ? responseValue : (responseValue ? [responseValue] : []);
          console.log('Checkbox value prepared:', value);
          return {
            question_id: question.id,
            value: value
          };
        }
        
        // For other types, send the actual value
        // Keep empty strings as empty strings (don't convert to null)
        // Only convert undefined/null to null
        let value = responseValue;
        
        // If value is undefined, send null
        // Otherwise, send the actual value (even if it's an empty string)
        if (value === undefined) {
          value = null;
        }
        
        console.log('Non-checkbox value prepared:', { 
          original: responseValue, 
          final: value,
          type: typeof value,
          isString: typeof value === 'string'
        });
        
        return {
          question_id: question.id,
          value: value
        };
      });

      // Prepare respondent info
      const submitData = {
        responses: responseData,
        respondent_name: respondentInfo.name || null,
        respondent_email: respondentInfo.email || null,
        respondent_phone: respondentInfo.phone || null,
      };
      
      // Add custom fields
      if (currentSurvey.custom_respondent_fields && currentSurvey.custom_respondent_fields.length > 0) {
        submitData.respondent_custom = {};
        currentSurvey.custom_respondent_fields.forEach(field => {
          if (respondentInfo.custom[field.name]) {
            submitData.respondent_custom[field.name] = respondentInfo.custom[field.name];
          }
        });
      }

      console.log('=== FRONTEND: Submitting Survey ===');
      console.log('Survey ID:', currentSurvey.id);
      console.log('Total questions:', currentSurvey.questions.length);
      console.log('Response data being sent:', responseData);
      console.log('Full submit data:', submitData);
      
      // Log each response in detail
      responseData.forEach((resp, idx) => {
        const question = currentSurvey.questions.find(q => q.id === resp.question_id);
        console.log(`Response #${idx}:`, {
          question_id: resp.question_id,
          question_text: question?.question_text || 'UNKNOWN',
          question_type: question?.question_type || 'UNKNOWN',
          value: resp.value,
          value_type: typeof resp.value,
          is_null: resp.value === null,
          is_undefined: resp.value === undefined,
          is_empty_string: resp.value === ''
        });
      });

      const response = await surveyAPI.submitSurvey(currentSurvey.id, submitData);
      
      console.log('=== FRONTEND: Submission Response ===');
      console.log('Status:', response.status);
      console.log('Data:', response.data);

      setSubmitted(true);
      toast.success('Survey submitted successfully! Thank you for your response.');
    } catch (error) {
      console.error('Error submitting survey:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors ? 
                          JSON.stringify(error.response.data.errors) :
                          error.message || 
                          'Failed to submit survey';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getProgress = () => {
    if (!currentSurvey || !currentSurvey.questions) return 0;
    return ((currentQuestionIndex + 1) / currentSurvey.questions.length) * 100;
  };

  const renderQuestion = (question) => {
    const questionNumber = currentSurvey.questions.findIndex(q => q.id === question.id) + 1;
    const currentResponse = responses[question.id];

    return (
      <QuestionCard key={question.id} theme={theme}>
        <QuestionText theme={theme}>
          {questionNumber}. {question.question_text}
          {question.is_required && <RequiredBadge>*</RequiredBadge>}
        </QuestionText>
        {question.help_text && (
          <HelpText theme={theme}>{question.help_text}</HelpText>
        )}

        {question.question_type === 'text' && (
          <Input
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Enter your answer"
            required={question.is_required}
          />
        )}

        {question.question_type === 'textarea' && (
          <Textarea
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Enter your answer"
            required={question.is_required}
          />
        )}

        {question.question_type === 'radio' && (
          <OptionsContainer>
            {question.options?.map((option, index) => (
              <OptionLabel key={index} theme={theme}>
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={currentResponse === option}
                  onChange={() => handleResponseChange(question.id, option)}
                  required={question.is_required}
                />
                <span>{option}</span>
              </OptionLabel>
            ))}
          </OptionsContainer>
        )}

        {question.question_type === 'checkbox' && (
          <OptionsContainer>
            {question.options?.map((option, index) => (
              <OptionLabel key={index} theme={theme}>
                <input
                  type="checkbox"
                  value={option}
                  checked={Array.isArray(currentResponse) && currentResponse.includes(option)}
                  onChange={(e) => {
                    const current = Array.isArray(currentResponse) ? currentResponse : [];
                    if (e.target.checked) {
                      handleResponseChange(question.id, [...current, option]);
                    } else {
                      handleResponseChange(question.id, current.filter(v => v !== option));
                    }
                  }}
                />
                <span>{option}</span>
              </OptionLabel>
            ))}
          </OptionsContainer>
        )}

        {question.question_type === 'select' && (
          <select
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            required={question.is_required}
            style={{
              width: '100%',
              padding: '0.75rem',
              marginTop: '1rem',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.base,
            }}
          >
            <option value="">Select an option</option>
            {question.options?.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        )}

        {question.question_type === 'number' && (
          <Input
            type="number"
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Enter a number"
            required={question.is_required}
          />
        )}

        {question.question_type === 'email' && (
          <Input
            type="email"
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Enter your email"
            required={question.is_required}
          />
        )}

        {question.question_type === 'date' && (
          <Input
            type="date"
            value={currentResponse || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            required={question.is_required}
          />
        )}

        {question.question_type === 'rating' && (
          <div style={{ marginTop: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[1,2,3,4,5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleResponseChange(question.id, String(n))}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.border}`,
                    background: currentResponse === String(n) ? theme.colors.primary : 'transparent',
                    color: currentResponse === String(n) ? '#fff' : theme.colors.text.primary,
                    cursor: 'pointer'
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
            <HelpText theme={theme} style={{ marginTop: '0.5rem' }}>
              Select a rating from 1 (lowest) to 5 (highest)
            </HelpText>
          </div>
        )}
      </QuestionCard>
    );
  };

  if (loading) {
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content theme={theme}>
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <Loader size={48} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '1rem' }}>Loading...</p>
          </div>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  // Survey submitted view
  if (submitted) {
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content theme={theme}>
          <SurveyCard theme={theme}>
            <SuccessMessage>
              <CheckCircle size={64} style={{ color: theme.colors.success, marginBottom: '1rem' }} />
              <h2>Thank You!</h2>
              <p>Your survey response has been submitted successfully.</p>
              <Button
                type="button"
                variant="primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Reset all state before navigating
                  setCurrentSurvey(null);
                  setSurveyStarted(false);
                  setSubmitted(false);
                  setResponses({});
                  setCurrentQuestionIndex(0);
                  navigate('/surveys', { replace: true });
                }}
                style={{ marginTop: '2rem' }}
              >
                View Other Surveys
              </Button>
            </SuccessMessage>
          </SurveyCard>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  // Survey intro/start view (before clicking Attend) - This must come BEFORE the form view
  if (currentSurvey && !surveyStarted) {
    console.log('Rendering survey intro page', { 
      currentSurveyTitle: currentSurvey.title, 
      surveyStarted,
      hasQuestions: currentSurvey.questions?.length > 0,
      slug: slug
    });
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content theme={theme}>
          <SurveyCard theme={theme}>
            <SurveyTitle theme={theme}>{currentSurvey.title}</SurveyTitle>
            {currentSurvey.description && (
              <SurveyDescription theme={theme}>{currentSurvey.description}</SurveyDescription>
            )}
            {currentSurvey.instructions && (
              <div style={{ 
                marginTop: '1.5rem', 
                padding: '1rem', 
                background: theme.colors.background.secondary, 
                borderRadius: theme.borderRadius.md,
                borderLeft: `4px solid ${theme.colors.primary}`
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.lg, color: theme.colors.text.primary }}>
                  Instructions
                </h4>
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.base, color: theme.colors.text.secondary, whiteSpace: 'pre-line' }}>
                  {currentSurvey.instructions}
                </p>
              </div>
            )}
            
            
            <div style={{ marginTop: '2rem', padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FileText size={20} style={{ color: theme.colors.primary }} />
                <span style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                  {currentSurvey.questions?.length || 0} Questions
                </span>
              </div>
              {currentSurvey.show_progress !== false && (
                <p style={{ margin: '0.5rem 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  You'll see your progress as you complete the survey.
                </p>
              )}
            </div>

            <div style={{ marginTop: '3rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="ghost"
                onClick={() => navigate('/surveys')}
                style={{ minWidth: '120px' }}
              >
                Back to Surveys
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  console.log('Attend Survey button clicked');
                  handleStartSurvey();
                }}
                leftIcon={<ArrowRight size={18} />}
                style={{ 
                  minWidth: '180px', 
                  fontSize: theme.typography.fontSize.lg, 
                  padding: '0.875rem 2rem'
                }}
              >
                Attend Survey
              </Button>
            </div>
          </SurveyCard>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  // Survey list view
  if (!slug && !currentSurvey) {
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content theme={theme}>
          <SurveyCard theme={theme}>
            <SurveyTitle theme={theme}>Available Surveys</SurveyTitle>
            <SurveyDescription theme={theme}>
              Participate in our surveys and help us improve our services.
            </SurveyDescription>
          </SurveyCard>

          {surveys.length === 0 ? (
            <Card padding="3rem" textAlign="center">
              <FileText size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
              <p>No surveys available at this time.</p>
            </Card>
          ) : (
            <SurveyListContainer>
              {surveys.map(survey => (
                <SurveyListItem
                  key={survey.id}
                  theme={theme}
                >
                  <h3 style={{ margin: '0 0 0.5rem 0' }}>{survey.title}</h3>
                  <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                    {survey.description}
                  </p>
                  <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                      {survey.questions?.length || 0} questions
                    </span>
                    <Button
                      variant="primary"
                      onClick={() => navigate(`/surveys/${survey.slug}`)}
                      style={{ minWidth: '160px' }}
                    >
                      Attend Survey
                    </Button>
                  </div>
                </SurveyListItem>
              ))}
            </SurveyListContainer>
          )}
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  // Survey submitted view
  if (submitted) {
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content theme={theme}>
          <SurveyCard theme={theme}>
            <SuccessMessage>
              <CheckCircle size={64} style={{ color: theme.colors.success, marginBottom: '1rem' }} />
              <h2>Thank You!</h2>
              <p>Your survey response has been submitted successfully.</p>
              <Button
                type="button"
                variant="primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Reset all state before navigating
                  setCurrentSurvey(null);
                  setSurveyStarted(false);
                  setSubmitted(false);
                  setResponses({});
                  setCurrentQuestionIndex(0);
                  navigate('/surveys', { replace: true });
                }}
                style={{ marginTop: '2rem' }}
              >
                View Other Surveys
              </Button>
            </SuccessMessage>
          </SurveyCard>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  // Survey intro/start view (before clicking Attend)
  if (currentSurvey && !surveyStarted) {
    console.log('Rendering survey intro page', { currentSurvey: currentSurvey.title, surveyStarted });
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content theme={theme}>
          <SurveyCard theme={theme}>
            <SurveyTitle theme={theme}>{currentSurvey.title}</SurveyTitle>
            {currentSurvey.description && (
              <SurveyDescription theme={theme}>{currentSurvey.description}</SurveyDescription>
            )}
            {currentSurvey.instructions && (
              <div style={{ 
                marginTop: '1.5rem', 
                padding: '1rem', 
                background: theme.colors.background.secondary, 
                borderRadius: theme.borderRadius.md,
                borderLeft: `4px solid ${theme.colors.primary}`
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.lg, color: theme.colors.text.primary }}>
                  Instructions
                </h4>
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.base, color: theme.colors.text.secondary, whiteSpace: 'pre-line' }}>
                  {currentSurvey.instructions}
                </p>
              </div>
            )}
            
            <div style={{ marginTop: '2rem', padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FileText size={20} style={{ color: theme.colors.primary }} />
                <span style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                  {currentSurvey.questions?.length || 0} Questions
                </span>
              </div>
              {currentSurvey.show_progress !== false && (
                <p style={{ margin: '0.5rem 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  You'll see your progress as you complete the survey.
                </p>
              )}
            </div>

            <div style={{ marginTop: '3rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="ghost"
                onClick={() => navigate('/surveys')}
                style={{ minWidth: '120px' }}
              >
                Back to Surveys
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  console.log('Attend Survey button clicked');
                  handleStartSurvey();
                }}
                leftIcon={<ArrowRight size={18} />}
                style={{ 
                  minWidth: '180px', 
                  fontSize: theme.typography.fontSize.lg, 
                  padding: '0.875rem 2rem'
                }}
              >
                Attend Survey
              </Button>
            </div>
          </SurveyCard>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  // Survey form view (after clicking Attend)
  const currentQuestion = currentSurvey?.questions?.[currentQuestionIndex];

  return (
    <Container theme={theme}>
      <PublicHeader theme={theme} />
      <Content theme={theme}>
        {currentSurvey && (
          <>
            <SurveyCard theme={theme}>
              <SurveyTitle theme={theme}>{currentSurvey.title}</SurveyTitle>
              
              {currentSurvey.show_progress && (
                <ProgressBar theme={theme}>
                  <ProgressFill progress={getProgress()} theme={theme} />
                </ProgressBar>
              )}

              {/* Respondent info */}
              {currentSurvey.collect_respondent_info && currentQuestionIndex === 0 && (
                <div style={{ marginTop: '2rem', padding: '1.5rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, border: `2px solid ${theme.colors.primary}20` }}>
                  <h4 style={{ margin: '0 0 1rem 0', fontSize: theme.typography.fontSize.lg, color: theme.colors.text.primary }}>
                    Your Information
                  </h4>
                  <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginBottom: '1.5rem' }}>
                    Please provide the following information before starting the survey.
                  </p>
                  
                  {currentSurvey.collect_name && (
                    <FormGroup theme={theme} style={{ marginBottom: '1rem' }}>
                      <Label theme={theme}>
                        Name {currentSurvey.require_name && <RequiredBadge>*</RequiredBadge>}
                      </Label>
                      <Input
                        placeholder="Enter your name"
                        value={respondentInfo.name}
                        onChange={(e) => setRespondentInfo(prev => ({ ...prev, name: e.target.value }))}
                        required={currentSurvey.require_name}
                      />
                    </FormGroup>
                  )}
                  
                  {currentSurvey.collect_email && (
                    <FormGroup theme={theme} style={{ marginBottom: '1rem' }}>
                      <Label theme={theme}>
                        Email Address {currentSurvey.require_email && <RequiredBadge>*</RequiredBadge>}
                      </Label>
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={respondentInfo.email}
                        onChange={(e) => setRespondentInfo(prev => ({ ...prev, email: e.target.value }))}
                        required={currentSurvey.require_email}
                      />
                    </FormGroup>
                  )}
                  
                  {currentSurvey.collect_phone && (
                    <FormGroup theme={theme} style={{ marginBottom: '1rem' }}>
                      <Label theme={theme}>
                        Phone Number {currentSurvey.require_phone && <RequiredBadge>*</RequiredBadge>}
                      </Label>
                      <Input
                        type="tel"
                        placeholder="Enter your phone number"
                        value={respondentInfo.phone}
                        onChange={(e) => setRespondentInfo(prev => ({ ...prev, phone: e.target.value }))}
                        required={currentSurvey.require_phone}
                      />
                    </FormGroup>
                  )}
                  
                  {/* Custom Fields */}
                  {currentSurvey.custom_respondent_fields && currentSurvey.custom_respondent_fields.length > 0 && (
                    <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: `1px solid ${theme.colors.border}` }}>
                      {currentSurvey.custom_respondent_fields.map((field, index) => (
                        <FormGroup key={index} theme={theme} style={{ marginBottom: '1rem' }}>
                          <Label theme={theme}>
                            {field.label} {field.required && <RequiredBadge>*</RequiredBadge>}
                          </Label>
                          {field.type === 'select' && field.options && Array.isArray(field.options) && field.options.length > 0 ? (
                            <select
                              value={respondentInfo.custom[field.name] || ''}
                              onChange={(e) => setRespondentInfo(prev => ({
                                ...prev,
                                custom: { ...prev.custom, [field.name]: e.target.value }
                              }))}
                              required={field.required}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius.md,
                                fontSize: theme.typography.fontSize.base,
                                fontFamily: 'inherit',
                              }}
                            >
                              <option value="">Select {field.label}</option>
                              {field.options.map((option, idx) => (
                                <option key={idx} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : (
                            <Input
                              type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : 'text'}
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                              value={respondentInfo.custom[field.name] || ''}
                              onChange={(e) => setRespondentInfo(prev => ({
                                ...prev,
                                custom: { ...prev.custom, [field.name]: e.target.value }
                              }))}
                              required={field.required}
                            />
                          )}
                        </FormGroup>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </SurveyCard>

            {currentQuestion && renderQuestion(currentQuestion)}

            <NavigationButtons>
              <Button
                variant="ghost"
                leftIcon={<ArrowLeft size={16} />}
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </Button>

              {currentQuestionIndex === currentSurvey.questions.length - 1 ? (
                <Button
                  variant="primary"
                  rightIcon={<CheckCircle size={16} />}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Survey'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  rightIcon={<ArrowRight size={16} />}
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </NavigationButtons>
          </>
        )}
      </Content>
      <PublicFooter theme={theme} />
    </Container>
  );
};

export default SurveyPage;

